import React, { useState } from "react";
import { X, ShoppingBag, Calendar, MapPin, Download, Package, Loader2, ChevronDown, Music, FileAudio } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useNotification } from "../../context/NotificationContext";
import { UserDashboardService } from "../../services/user/UserDashboardService";

export default function OrderDetailsModal({ orderId, isOpen, onClose, fetchDetails }) {
    const { showNotification } = useNotification();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloadingIds, setDownloadingIds] = useState([]);
    const [activeQualityDropdown, setActiveQualityDropdown] = useState(null);

    const qualityOptions = [
        { label: "Original Format", value: "original", desc: "The source file uploaded", icon: <Music size={14} /> },
        { label: "High Quality MP3", value: "mp3_high", desc: "Crystal clear 320kbps", icon: <FileAudio size={14} /> },
        { label: "Standard MP3", value: "mp3_standard", desc: "Smaller size 128kbps", icon: <FileAudio size={14} /> },
        { label: "WAV Lossless", value: "wav", desc: "Studio quality uncompressed", icon: <FileAudio size={14} /> },
    ];

    React.useEffect(() => {
        if (isOpen && orderId) {
            setLoading(true);
            fetchDetails(orderId)
                .then(data => {
                    if (data.status === "success") {
                        setOrder(data.order);
                    }
                })
                .finally(() => setLoading(false));
        }
    }, [isOpen, orderId, fetchDetails]);

    const handleDownload = async (item, quality = "original", mediaType = null) => {
        const downloadKey = `${item.id}_${mediaType || 'default'}`;
        if (downloadingIds.includes(downloadKey)) return;
        setActiveQualityDropdown(null);

        setDownloadingIds(prev => [...prev, downloadKey]);
        try {
            const response = await UserDashboardService.downloadOrderItem(item.id, quality, mediaType);

            // Extract filename from Content-Disposition if available
            let filename = `${item.product_name.replace(/\s+/g, '_')}_digital_content`;
            const contentDisposition = response.headers['content-disposition'];
            if (contentDisposition) {
                const filenameMatch = contentDisposition.match(/filename="?(.+?)"?($|;)/);
                if (filenameMatch && filenameMatch[1]) {
                    filename = filenameMatch[1];
                }
            }

            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const blobUrl = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = blobUrl;
            link.download = filename;

            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(blobUrl);

            showNotification({
                type: "success",
                title: "Download Started",
                message: `${item.product_name} is being saved to your device.`
            });
        } catch (error) {
            console.error("Download error:", error);
            showNotification({
                type: "failed",
                title: "Download Failed",
                message: "We couldn't retrieve the file. Please try again later."
            });
        } finally {
            setDownloadingIds(prev => prev.filter(id => id !== downloadKey));
        }
    };

    if (!isOpen) return null;

    return (
        <AnimatePresence>
            <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    onClick={onClose}
                    className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                />

                <motion.div
                    initial={{ opacity: 0, scale: 0.95, y: 20 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95, y: 20 }}
                    className="relative w-full max-w-2xl bg-white rounded-3xl shadow-2xl overflow-hidden overflow-y-auto max-h-[90vh] font-sans"
                >
                    {/* Header */}
                    <div className="p-6 border-b border-black/5 flex items-center justify-between sticky top-0 bg-white z-10">
                        <div>
                            <h3 className="text-xl font-serif italic text-black">Order Details</h3>
                            {order && <p className="text-[10px] tracking-[2px] uppercase text-black/80 font-bold mt-1">Ref: {order.reference}</p>}
                        </div>
                        <button
                            onClick={onClose}
                            className="p-2 hover:bg-black/5 rounded-full transition-colors text-black/60 hover:text-black"
                        >
                            <X size={20} />
                        </button>
                    </div>

                    <div className="p-8">
                        {loading ? (
                            <div className="flex flex-col items-center justify-center py-20">
                                <Loader2 className="w-10 h-10 text-jazz-brown animate-spin mb-4" />
                                <p className="text-sm font-bold tracking-[1px] uppercase text-black/80">Fetching Order Details...</p>
                            </div>
                        ) : order ? (
                            <div className="space-y-8 animate-fade-in">
                                {/* Status & Summary */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 bg-[#fafafa] p-6 rounded-2xl border border-black/5">
                                    <div className="space-y-4">
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center text-black/60">
                                                <ShoppingBag size={16} />
                                            </div>
                                            <div>
                                                <p className="text-[9px] tracking-[1px] uppercase text-black/80 font-bold">Status</p>
                                                <span className={`text-[11px] font-bold tracking-[1px] uppercase ${order.status === 'paid' || order.status === 'completed' ? 'text-green-600' :
                                                    order.status === 'shipped' ? 'text-blue-600' :
                                                        order.status === 'pending' || order.status === 'processing' ? 'text-amber-600' :
                                                            order.status === 'failed' ? 'text-red-600' : 'text-black/60'
                                                    }`}>
                                                    {order.status}
                                                </span>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center text-black/60">
                                                <Calendar size={16} />
                                            </div>
                                            <div>
                                                <p className="text-[9px] tracking-[1px] uppercase text-black/80 font-bold">Placed On</p>
                                                <p className="text-sm font-semibold text-black">{new Date(order.created_at).toLocaleString()}</p>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="space-y-4">
                                        <div className="flex items-start gap-3">
                                            <div className="w-8 h-8 bg-black/5 rounded-lg flex items-center justify-center text-black/60 mt-1">
                                                <MapPin size={16} />
                                            </div>
                                            <div>
                                                <p className="text-[9px] tracking-[1px] uppercase text-black/80 font-bold">Shipping To</p>
                                                <p className="text-xs font-semibold text-black leading-relaxed whitespace-pre-wrap">
                                                    {order.shipping_address || "N/A (Digital Order)"}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Items */}
                                <div className="space-y-4">
                                    <h4 className="text-sm font-bold tracking-[2px] uppercase text-black/80">Ordered Items</h4>
                                    <div className="space-y-4">
                                        {order.items.map((item) => (
                                            <div key={item.id} className="flex gap-4 p-4 border border-black/5 rounded-2xl group hover:border-black/10 transition-colors">
                                                <div className="w-20 h-20 bg-black/5 rounded-xl overflow-hidden flex-shrink-0">
                                                    {item.image ? (
                                                        <img src={item.image} alt={item.product_name} className="w-full h-full object-cover" />
                                                    ) : (
                                                        <div className="w-full h-full flex items-center justify-center text-black/10">
                                                            <Package size={24} />
                                                        </div>
                                                    )}
                                                </div>
                                                <div className="flex-1 min-w-0 py-1">
                                                    <div className="flex justify-between items-start mb-2">
                                                        <h5 className="font-bold text-sm text-black truncate pr-4">{item.product_name}</h5>
                                                        <span className="text-sm font-bold text-jazz-brown">${item.subtotal.toFixed(2)}</span>
                                                    </div>
                                                    <div className="flex items-center gap-3 mb-4">
                                                        <p className="text-[10px] text-black/80 font-semibold">
                                                            Qty: {item.quantity} · ${item.unit_price.toFixed(2)} ea
                                                        </p>
                                                        <span className={`px-2 py-0.5 rounded text-[8px] font-bold tracking-[1px] uppercase ${item.status === 'completed' ? 'bg-green-100 text-green-700' :
                                                            item.status === 'shipped' ? 'bg-blue-100 text-blue-700' :
                                                                item.status === 'processing' ? 'bg-amber-100 text-amber-700' : 'bg-black/5 text-black/80'
                                                            }`}>
                                                            {item.status}
                                                        </span>
                                                    </div>

                                                    {item.available_digital_files && item.available_digital_files.length > 0 && ['paid', 'processing', 'shipped'].includes(order.status) && (
                                                        <div className="flex flex-wrap gap-3">
                                                            {item.available_digital_files.map((file) => {
                                                                const isAudio = file.type === 'audio';
                                                                const categoryType = (item.category_type || '').toLowerCase();
                                                                const downloadKey = `${item.id}_${file.type}`;

                                                                let label = 'Digital Content';
                                                                if (file.type === 'audio') {
                                                                    label = categoryType.includes('album') ? 'Full Album' : 'Music';
                                                                } else if (file.type === 'pdf') {
                                                                    label = 'Sheet Music';
                                                                } else if (file.type === 'zip') {
                                                                    label = 'Full Album';
                                                                }

                                                                return (
                                                                    <div key={file.id} className="relative">
                                                                        <div className="flex items-center gap-0">
                                                                            <button
                                                                                onClick={() => isAudio ? setActiveQualityDropdown(activeQualityDropdown === downloadKey ? null : downloadKey) : handleDownload(item, 'original', file.type)}
                                                                                disabled={downloadingIds.includes(downloadKey)}
                                                                                className={`inline-flex items-center gap-2 py-2 px-3 bg-black text-white rounded-lg text-[9px] font-bold tracking-[1px] uppercase transition-all shadow-sm disabled:bg-black/20 disabled:cursor-not-allowed ${isAudio ? 'rounded-r-none hover:bg-black' : 'hover:bg-jazz-brown'}`}
                                                                            >
                                                                                {downloadingIds.includes(downloadKey) ? (
                                                                                    <Loader2 size={12} className="animate-spin" />
                                                                                ) : (
                                                                                    <Download size={12} />
                                                                                )}
                                                                                {downloadingIds.includes(downloadKey) ? 'Downloading...' : `Download ${label}`}
                                                                            </button>

                                                                            {isAudio && (
                                                                                <button
                                                                                    onClick={() => setActiveQualityDropdown(activeQualityDropdown === downloadKey ? null : downloadKey)}
                                                                                    disabled={downloadingIds.includes(downloadKey)}
                                                                                    className="py-2 px-2 bg-black text-white rounded-lg rounded-l-none hover:bg-jazz-brown border-l border-white/10 transition-colors disabled:bg-black/20"
                                                                                >
                                                                                    <ChevronDown size={14} className={`transition-transform duration-200 ${activeQualityDropdown === downloadKey ? 'rotate-180' : ''}`} />
                                                                                </button>
                                                                            )}
                                                                        </div>

                                                                        {/* Quality Dropdown */}
                                                                        <AnimatePresence>
                                                                            {activeQualityDropdown === downloadKey && (
                                                                                <>
                                                                                    <div
                                                                                        className="fixed inset-0 z-10"
                                                                                        onClick={() => setActiveQualityDropdown(null)}
                                                                                    />
                                                                                    <motion.div
                                                                                        initial={{ opacity: 0, y: 10, scale: 0.95 }}
                                                                                        animate={{ opacity: 1, y: 0, scale: 1 }}
                                                                                        exit={{ opacity: 0, y: 10, scale: 0.95 }}
                                                                                        className="absolute left-0 mt-2 w-64 bg-white rounded-xl shadow-xl border border-black/5 z-20 overflow-hidden"
                                                                                    >
                                                                                        <div className="p-3 border-b border-black/5">
                                                                                            <p className="text-[10px] uppercase tracking-[2px] font-black text-jazz-brown">Select Quality</p>
                                                                                        </div>
                                                                                        <div className="py-1">
                                                                                            {qualityOptions.map((opt) => (
                                                                                                <button
                                                                                                    key={opt.value}
                                                                                                    onClick={() => handleDownload(item, opt.value, 'audio')}
                                                                                                    className="w-full text-left px-4 py-3 hover:bg-[#fafafa] flex items-start gap-3 group transition-colors"
                                                                                                >
                                                                                                    <div className="mt-0.5 text-black/20 group-hover:text-jazz-brown transition-colors">
                                                                                                        {opt.icon}
                                                                                                    </div>
                                                                                                    <div>
                                                                                                        <p className="text-[11px] font-bold text-black group-hover:text-jazz-brown transition-colors">{opt.label}</p>
                                                                                                        <p className="text-[9px] text-black/80 font-medium">{opt.desc}</p>
                                                                                                    </div>
                                                                                                </button>
                                                                                            ))}
                                                                                        </div>
                                                                                    </motion.div>
                                                                                </>
                                                                            )}
                                                                        </AnimatePresence>
                                                                    </div>
                                                                );
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="text-center py-20">
                                <p className="text-sm text-black/40 font-bold uppercase tracking-[1px]">Something went wrong</p>
                            </div>
                        )}

                        {/* Footer Total */}
                        {order && (
                            <div className="border-t border-black/5 pt-6 mt-8 flex justify-between items-center">
                                <p className="text-sm font-serif italic text-black/80">Total Amount Paid</p>
                                <p className="text-2xl font-bold text-black font-serif italic">${order.amount.toFixed(2)}</p>
                            </div>
                        )}
                    </div>
                </motion.div>
            </div>
        </AnimatePresence>
    );
}